// components/common/UploadButton.tsx
import clsx from 'clsx';
import React, { useRef, useState } from 'react';

interface UploadButtonProps {
    label?: string;
    accept?: string; // e.g. ".csv,.xlsx"
    maxSizeMB?: number; // e.g. 10
    onUpload?: (file: File) => Promise<void> | void; // API call yahan se
    className?: string;
}

const UploadButton: React.FC<UploadButtonProps> = ({
    label = 'Upload',
    accept = '',
    maxSizeMB = 10,
    onUpload,
    className,
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openFilePicker = () => {
        if (!isUploading) {
            inputRef.current?.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // reset input so same file dobara choose kar sakte ho
        e.target.value = '';

        // size check
        const sizeInMB = file.size / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
            setError(`Max file size ${maxSizeMB}MB allowed`);
            return;
        }

        setError(null);

        if (!onUpload) return; // agar sirf file select chahiye to yahin tak

        try {
            setIsUploading(true);
            await onUpload(file);
        } catch (err: any) {
            console.error(err);
            setError(err?.message || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="flex flex-col items-end gap-1">
            <button
                type="button"
                onClick={openFilePicker}
                disabled={isUploading}
                className={clsx(
                    'inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2',
                    'text-sm font-medium text-white shadow-sm',
                    'hover:bg-blue-700 focus:ring-2 focus:outline-none',
                    'transition focus:ring-blue-200 active:scale-[0.98]',
                    isUploading && 'cursor-not-allowed opacity-80',
                    className,
                )}
            >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/70">
                    {/* upload icon */}
                    <svg
                        className="h-3.5 w-3.5"
                        viewBox="0 0 20 20"
                        fill="none"
                    >
                        <path
                            d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 13v3h12v-3"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <span>{isUploading ? 'Uploading...' : label}</span>
            </button>

            {/* hidden file input */}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={handleFileChange}
            />

            {/* simple error text – chahe to apne toast/message se replace kar */}
            {error && (
                <span className="mt-0.5 text-xs text-red-500">{error}</span>
            )}
        </div>
    );
};

export default UploadButton;
